/**
 * Normalizes text by collapsing all whitespace (spaces, newlines, tabs) into single spaces
 * and trimming the result. This helps with matching text that may have different formatting.
 */
export function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Normalizes text for flexible matching by:
 * - Replacing special quotes and apostrophes with standard versions
 * - Collapsing whitespace
 * - Converting to lowercase (for case-insensitive matching)
 */
export function normalizeForMatching(text: string): string {
  return (
    text
      // Normalize quotes first
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      // Normalize dashes
      .replace(/[–—]/g, "-")
      // Normalize ellipsis
      .replace(/…/g, "...")
      // Collapse whitespace
      .replace(/\s+/g, " ")
      .trim()
      // Convert to lowercase last
      .toLowerCase()
  );
}

/**
 * Normalizes special characters like quotes, dashes, and ellipsis
 * This changes the string length, so must be done carefully
 * Note: Treats all quote types (single, double, curly) as equivalent to handle variations
 */
function normalizeSpecialChars(text: string): string {
  return (
    text
      // Normalize ALL quote types to standard double quote for flexible matching
      // This includes: curly double quotes, curly single quotes, straight single quotes
      .replace(/[""'']/g, '"')
      .replace(/[–—]/g, "-")
      .replace(/…/g, "...")
  );
}

/**
 * Strips leading and trailing punctuation from a string, useful for flexible matching.
 * This helps match strings that might have punctuation differences at boundaries.
 */
function stripBoundaryPunctuation(text: string): string {
  // Remove common punctuation from start and end
  return text
    .replace(/^["'!?.,;:—–-]+\s*/, "")
    .replace(/\s*["'!?.,;:—–-]+$/, "");
}

/**
 * Builds a map from char-normalized positions to original positions
 */
function buildCharNormalizationMap(
  originalText: string,
  charNormalizedText: string,
): number[] {
  const map: number[] = [];
  let origIdx = 0;
  let normIdx = 0;

  while (origIdx < originalText.length && normIdx < charNormalizedText.length) {
    map[normIdx] = origIdx;

    const origChar = originalText[origIdx];
    const normChar = charNormalizedText[normIdx];

    // Special characters that get normalized
    // All quote types (single, double, curly) -> normalized to "
    if (
      (origChar === "\u201C" || // Left double quotation mark
        origChar === "\u201D" || // Right double quotation mark
        origChar === "\u2018" || // Left single quotation mark
        origChar === "\u2019" || // Right single quotation mark/apostrophe
        origChar === "'" || // Straight single quote/apostrophe
        origChar === '"') && // Straight double quote
      normChar === '"'
    ) {
      // All quote variations normalize to "
      origIdx += 1;
      normIdx += 1;
    } else if (
      (origChar === "\u2013" || origChar === "\u2014") &&
      normChar === "-"
    ) {
      // En dash and em dash
      origIdx += 1;
      normIdx += 1;
    } else if (
      origChar === "\u2026" &&
      charNormalizedText.substr(normIdx, 3) === "..."
    ) {
      // Ellipsis
      origIdx += 1;
      normIdx += 3;
    } else {
      // Regular character
      origIdx += 1;
      normIdx += 1;
    }
  }

  map[normIdx] = origIdx;
  return map;
}

/**
 * Maps a position in whitespace-normalized text back to the original text.
 * This accounts for the fact that multiple whitespace characters become single spaces.
 */
function mapWhitespaceNormalizedToOriginal(
  originalText: string,
  normalizedPos: number,
): number {
  let originalIndex = 0;
  let normalizedIndex = 0;
  let inWhitespace = false;

  while (
    originalIndex < originalText.length &&
    normalizedIndex < normalizedPos
  ) {
    const char = originalText[originalIndex];
    const isWhitespace = /\s/.test(char);

    if (isWhitespace) {
      if (!inWhitespace) {
        // First whitespace character in a sequence
        normalizedIndex += 1;
        inWhitespace = true;
      }
      // Additional whitespace characters don't increment normalized index
    } else {
      // Non-whitespace character
      normalizedIndex += 1;
      inWhitespace = false;
    }

    originalIndex += 1;
  }

  return originalIndex;
}

/**
 * Finds all occurrences of a search string within a target text using normalized matching.
 * Returns the positions in the ORIGINAL text where matches occur.
 * Uses flexible matching to handle special characters, case differences, and whitespace.
 */
export function findAllOccurrences(
  originalText: string,
  searchString: string,
): Array<{ start: number; end: number }> {
  const positions: Array<{ start: number; end: number }> = [];

  // Trim empty lines from beginning and end of search string
  const trimmedSearch = searchString.trim();

  if (!trimmedSearch) {
    return positions;
  }

  // Try multiple variations of the search string for flexible matching:
  // 1. Original trimmed version
  // 2. Version with quotes stripped from boundaries
  // 3. Version with all boundary punctuation stripped
  const strippedQuotes = trimmedSearch.replace(/^["']|["']$/g, "");
  const strippedPunctuation = stripBoundaryPunctuation(trimmedSearch);

  // Create array of variations, removing duplicates
  const searchVariations = [
    trimmedSearch,
    strippedQuotes,
    strippedPunctuation,
  ].filter((s, i, arr) => s && arr.indexOf(s) === i);

  // Normalize the text we're searching in
  const charNormalizedText = normalizeSpecialChars(originalText);
  const whiteSpaceNormalizedText = normalizeWhitespace(charNormalizedText);
  const fullyNormalizedText = whiteSpaceNormalizedText.toLowerCase();

  // Try each search variation until we find a match
  let charNormalizedSearch = "";
  let whiteSpaceNormalizedSearch = "";
  let fullyNormalizedSearch = "";

  searchVariations.forEach((searchVariation) => {
    if (fullyNormalizedSearch) {
      // Already found a match, skip remaining variations
      return;
    }

    charNormalizedSearch = normalizeSpecialChars(searchVariation);
    whiteSpaceNormalizedSearch = normalizeWhitespace(charNormalizedSearch);
    fullyNormalizedSearch = whiteSpaceNormalizedSearch.toLowerCase();

    if (
      fullyNormalizedSearch &&
      fullyNormalizedText.indexOf(fullyNormalizedSearch) !== -1
    ) {
      // Found a match with this variation, use it
      // (will exit loop on next iteration check)
    } else {
      // Reset if this variation didn't match
      fullyNormalizedSearch = "";
    }
  });

  // If no variation matched, return empty positions
  if (
    !fullyNormalizedSearch ||
    fullyNormalizedText.indexOf(fullyNormalizedSearch) === -1
  ) {
    return positions;
  }

  // Find all occurrences in normalized text
  let searchIndex = 0;
  while (searchIndex < fullyNormalizedText.length) {
    const foundIndex = fullyNormalizedText.indexOf(
      fullyNormalizedSearch,
      searchIndex,
    );
    if (foundIndex === -1) break;

    // Map back through the normalization steps:
    // 1. From fully normalized (with case) -> whitespace normalized (no case change affects position)
    // 2. From whitespace normalized -> char normalized (use mapNormalizedToOriginal)
    const charNormStart = mapWhitespaceNormalizedToOriginal(
      charNormalizedText,
      foundIndex,
    );
    const charNormEnd = mapWhitespaceNormalizedToOriginal(
      charNormalizedText,
      foundIndex + fullyNormalizedSearch.length,
    );

    // 3. From char normalized -> original (use character position map)
    const charPosMap = buildCharNormalizationMap(
      originalText,
      charNormalizedText,
    );
    const start = charPosMap[charNormStart] ?? charNormStart;
    const end = charPosMap[charNormEnd] ?? charNormEnd;

    // Trim any leading/trailing whitespace from the matched region
    let trimmedStart = start;
    let trimmedEnd = Math.min(end, originalText.length);

    // Trim leading whitespace
    while (trimmedStart < trimmedEnd && /\s/.test(originalText[trimmedStart])) {
      trimmedStart += 1;
    }

    // Trim trailing whitespace
    while (
      trimmedEnd > trimmedStart &&
      /\s/.test(originalText[trimmedEnd - 1])
    ) {
      trimmedEnd -= 1;
    }

    if (trimmedStart < trimmedEnd) {
      positions.push({ start: trimmedStart, end: trimmedEnd });
    }

    searchIndex = foundIndex + fullyNormalizedSearch.length;
  }

  return positions;
}
